<template>
  <Teleport to="body">
    <div class="fixed top-6 right-6 z-[9999] space-y-4 pointer-events-none perspective-1000">
      <TransitionGroup 
        name="toast"
        @enter="onEnter"
        @leave="onLeave"
      >
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="pointer-events-auto group relative"
        >
          <!-- Toast Card -->
          <div
            class="relative w-80 sm:w-96 bg-white/70 dark:bg-slate-900/80 backdrop-blur-2xl rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.12)] border border-white dark:border-white/10 overflow-hidden transform transition-all duration-500"
            :class="[
              toast.type === 'error' ? 'shadow-red-500/10' : 
              toast.type === 'success' ? 'shadow-emerald-500/10' : 
              'shadow-indigo-500/10'
            ]"
          >
            <!-- Background Glow -->
            <div 
              class="absolute -right-10 -top-10 w-32 h-32 rounded-full blur-[40px] opacity-20 pointer-events-none transition-all duration-1000 group-hover:scale-150"
              :class="iconBgClass(toast.type)"
            ></div>

            <div class="flex items-start gap-4 p-5 relative z-10">
              <!-- Animated Icon Container -->
              <div
                class="flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg transform transition-transform group-hover:scale-110 active:scale-95 relative"
                :class="iconBgClass(toast.type)"
              >
                <div class="absolute inset-0 bg-white/30 rounded-2xl blur-sm opacity-50 pulse-slow"></div>
                <Icon :name="iconName(toast.type)" class="w-6 h-6 text-white relative z-10" />
              </div>

              <!-- Content Hub -->
              <div class="flex-1 min-w-0 pt-0.5">
                <h4 class="text-sm font-black text-gray-900 dark:text-white uppercase tracking-wider">
                  {{ toast.title }}
                </h4>
                <p v-if="toast.message" class="text-xs font-bold text-gray-500 dark:text-gray-400 mt-1 leading-relaxed line-clamp-2">
                  {{ toast.message }}
                </p>
              </div>

              <!-- Close Controller -->
              <button
                @click="removeToast(toast.id)"
                class="flex-shrink-0 w-8 h-8 rounded-xl bg-gray-100 dark:bg-white/5 flex items-center justify-center text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white dark:hover:bg-white/10 transition-all border border-transparent hover:border-gray-200 dark:hover:border-white/10"
              >
                <Icon name="heroicons:x-mark" class="w-4 h-4" />
              </button>
            </div>

            <!-- Precision Progress Indicator -->
            <div
              v-if="toast.duration"
              class="absolute bottom-0 left-0 h-1 transition-all ease-linear shadow-[0_0_10px_rgba(255,255,255,0.5)]"
              :class="progressClass(toast.type)"
              :style="{ width: toast.progress + '%' }"
            ></div>
          </div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue';
import { useToastStore } from '~/stores/toast';

const toastStore = useToastStore();
const toasts = computed(() => toastStore.toasts);

const removeToast = (id) => {
  toastStore.remove(id);
};

const typeConfig = {
  success: {
    icon: 'heroicons:check-badge-solid',
    bg: 'bg-gradient-to-br from-emerald-500 to-teal-400',
    progress: 'bg-emerald-500 shadow-emerald-500/50'
  },
  error: {
    icon: 'heroicons:exclamation-circle-solid',
    bg: 'bg-gradient-to-br from-red-500 to-rose-400',
    progress: 'bg-red-500 shadow-red-500/50'
  },
  warning: {
    icon: 'heroicons:shield-exclamation-solid',
    bg: 'bg-gradient-to-br from-amber-500 to-orange-400',
    progress: 'bg-amber-500 shadow-amber-500/50'
  },
  info: {
    icon: 'heroicons:information-circle-solid',
    bg: 'bg-gradient-to-br from-indigo-500 to-blue-400',
    progress: 'bg-indigo-500 shadow-indigo-500/50'
  }
};

const iconName = (type) => typeConfig[type]?.icon || typeConfig.info.icon;
const iconBgClass = (type) => typeConfig[type]?.bg || typeConfig.info.bg;
const progressClass = (type) => typeConfig[type]?.progress || typeConfig.info.progress;

// Transition Handlers for micro-animations
const onEnter = (el) => {
  el.style.transform = 'translateY(-20px) scale(0.9)';
};

const onLeave = (el) => {
  el.style.position = 'absolute';
};
</script>

<style scoped>
.toast-enter-active {
  transition: all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.toast-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100px) scale(0.8) rotate(5deg);
  filter: blur(10px);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100px) scale(0.9);
  filter: blur(4px);
}

.toast-move {
  transition: transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes pulse-slow {
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.1); }
}

.pulse-slow {
  animation: pulse-slow 3s infinite ease-in-out;
}

.perspective-1000 {
  perspective: 1000px;
}
</style>
